/**
 * 변경이력 (audit_log) 한글 라벨 매핑.
 *
 * DB 테이블/컬럼명을 사용자가 알아보기 쉬운 한글 메뉴명·항목명으로 변환.
 * AuditLogTab 에서만 사용. 새 컬럼이 추적 화이트리스트에 추가되면 본 파일도 갱신.
 */

/** 테이블별 한글 메뉴명 + 식별자 필드 (PK 옆에 부가 표시할 사람 친화적 필드 우선순위) */
export const TABLE_META: Record<
  string,
  { menu: string; identifierFields: string[] }
> = {
  sales:                { menu: '영업진행',     identifierFields: ['end_user', 'proposal_company', 'project_name'] },
  purchase:             { menu: '매입',         identifierFields: ['item_name', 'description', 'supplier'] },
  maintenance_sales:    { menu: '유지보수',     identifierFields: ['contract_name', 'vendor_name'] },
  maintenance_contract: { menu: '유지보수 계약', identifierFields: ['end_user', 'contract_name'] },
  // v2.279.0 — 유지보수 계약 변경 이력 (연도별 금액 · 하도급)
  maintenance_year_amount: { menu: '유지보수 연도별 금액', identifierFields: [] },
  maintenance_subcontract: { menu: '유지보수 하도급',     identifierFields: ['supplier', 'item_name'] },
  emergency_bell_sales: { menu: '비상벨',       identifierFields: ['site_name', 'end_user'] },
  app_user:             { menu: '사용자 계정',   identifierFields: ['user_id', 'email'] },
  employee:             { menu: '직원',         identifierFields: ['name', 'emp_id'] },
  price_list:           { menu: '가격표',       identifierFields: ['item_code'] },
  stock_release:        { menu: '출고 등록',     identifierFields: ['use_for_end_user', 'use_for_project_name', 'use_for_text'] },
  stock_release_item:   { menu: '출고 항목',     identifierFields: ['item_name', 'item_code'] },
  item_master:          { menu: '품목 마스터',   identifierFields: ['item_name', 'item_code'] },
  data_load:            { menu: '데이터 재적재', identifierFields: [] },
  deal_registration:    { menu: '딜등록',       identifierFields: ['registration_no', 'project_id', 'end_user'] },
  // v2.143.0 에서 거래처 담당자 주소록(partner_contact)으로 통합 — 옛 이력은 이 이름으로 남아 보인다
  deal_partner_contact: { menu: '딜등록 제안사 명단 (옛)', identifierFields: ['company_kr', 'company_en', 'contact_name'] },
  deal_info_request:    { menu: '딜등록 정보 요청', identifierFields: ['company', 'project_name', 'end_user'] },
  partner_contact:      { menu: '거래처 담당자',   identifierFields: ['company_name', 'name'] },
  // v2.231.0 (점검 F-19) — 가격표·나라장터·수출입·개발 계획에도 변경 이력 (보기만 · 되돌리기 없음)
  main_product_price:    { menu: '가격표(주요 제품)', identifierFields: ['name', 'category'] },
  g2b_mas_product:       { menu: '나라장터 MAS',     identifierFields: ['full_name', 'g2b_id'] },
  g2b_third_party_price: { menu: '나라장터 3자단가', identifierFields: ['spec_text', 'g2b_id'] },
  g2b_settlement_rate:   { menu: '나라장터 정산요율', identifierFields: ['vendor', 'spec'] },
  g2b_bom_template:      { menu: '나라장터 구성(BOM)', identifierFields: ['target_key'] },
  cost_sheet:            { menu: '가격표(원가분석표)', identifierFields: ['title', 'customer'] },
  g2b_document:          { menu: '관급내역서·정산서', identifierFields: ['title', 'filename'] },
  import_order:          { menu: '수출입 오더',     identifierFields: ['ref_no', 'po_no'] },
  import_cost:           { menu: '수출입 비용',     identifierFields: ['detail', 'kind'] },
  import_cost_alloc:     { menu: '수출입 비용 배분', identifierFields: [] },
  import_cost_line:      { menu: '수출입 선적별 배분', identifierFields: [] },
  import_dhl:            { menu: '수출입 DHL',      identifierFields: ['bl_no', 'item_desc'] },
  dev_plan_target:       { menu: '개발 계획 대상',   identifierFields: ['label', 'target_key'] },
  dev_plan_note:         { menu: '개발 계획 글',     identifierFields: ['status'] },
  dev_plan_note_image:   { menu: '개발 계획 그림',   identifierFields: ['mime_type'] },
  dev_request:           { menu: '개발요청',         identifierFields: ['title', 'status'] },
  dev_request_comment:   { menu: '개발요청 댓글',    identifierFields: ['request_id'] },
  dev_request_image:     { menu: '개발요청 그림',    identifierFields: ['mime_type'] },
};

/** 테이블별 필드 한글 라벨 */
const FIELD_LABELS: Record<string, Record<string, string>> = {
  // v2.231.0 (점검 F-19)
  main_product_price: {
    category: '분류', name: '제품명', description: '설명',
    retail_price: '소비자가', supply_price: '공급가', sort_order: '정렬순서',
  },
  g2b_mas_product: {
    category: '분류', full_name: '품명', g2b_id: '식별번호', price_text: '가격',
    delivery_days: '납품기한', end_date: '계약종료일', sort_order: '정렬순서',
    image_mime: '사진 형식', image_filename: '사진 파일명', image_updated_at: '사진 바꾼 때',
  },
  g2b_third_party_price: {
    ctype: '구분', cat: '분류', spec_text: '규격', g2b_id: '식별번호', price: '단가',
    vendors: '업체', sort_order: '정렬순서',
    image_mime: '사진 형식', image_filename: '사진 파일명', image_updated_at: '사진 바꾼 때',
  },
  g2b_settlement_rate: {
    kind: '구분', vendor: '업체', spec: '규격', price: '단가', fee: '수수료', rate: '요율', fee_fixed: '수수료 직접 입력', note: '비고', sort_order: '정렬순서',
    volume_discount: '다량납품할인 기준',
  },
  g2b_bom_template: { target_kind: '대상 종류', target_key: '대상', items: '구성 품목' },
  cost_sheet: { title: '제목', customer: '고객', memo: '메모', margin_pct: '마진율(%)', round_unit: '판매단가 올림 단위', lines: '줄', quote_id: '견적서', sales_id: '영업건' },
  g2b_document: {
    doc_type: '문서 종류', title: '제목', filename: '파일명', payload: '입력 내용',
    total_amount: '합계', linked_sales_id: '연동 영업건', sales_attachment_id: '영업건 사본',
  },
  import_order: {
    ref_no: '관리번호', po_no: 'PO 번호', order_date: '발주일', is_rma: 'RMA',
    invoice_date: 'Invoice 일자', remit_due_date: '송금기한',
  },
  import_cost: {
    kind: '비용 종류', cost_date: '비용일', fx_rate: '환율', total_krw: '금액(원)', detail: '내역',
    nts_approval_no: '국세청 승인번호', from_purchase: '옮겨 온 매입', source_expense_doc_id: '지출결의서',
  },
  import_cost_alloc: { import_cost_id: '비용', import_order_id: '오더', usd_amount: '송금액($)' },
  import_cost_line: { import_cost_id: '비용', purchase_id: '매입 줄' },
  import_dhl: {
    import_order_id: '오더', direction: '방향', ship_date: '선적일', bl_no: 'B/L', freight_krw: '운임(원)',
    duty_krw: '관세(원)', due_date: '납부기한', item_desc: '품목', source_expense_doc_id: '지출결의서', auto_created: '자동 생성',
  },
  dev_plan_target: { target_key: '대상 열쇠', kind: '종류', label: '이름', sort_order: '정렬순서' },
  dev_plan_note: { target_id: '대상', doc: '본문', status: '진행 상태', status_by: '상태 바꾼 사람' },
  dev_plan_note_image: { note_id: '글', mime_type: '형식', width: '너비', height: '높이', file_size: '크기' },
  dev_request: { menu_key: '메뉴', title: '제목', doc: '본문', status: '상태', status_by: '상태 바꾼 사람', status_at: '상태 바꾼 때' },
  dev_request_comment: { request_id: '요청', body: '내용', author: '쓴 사람', is_bot: '루틴(🤖)', status_to: '바꾼 상태' },
  dev_request_image: { request_id: '요청', mime_type: '형식', width: '너비', height: '높이', file_size: '크기' },
  // 딜등록(인포트렌드) v2.140.0
  deal_registration: {
    vendor:          '벤더',
    registration_no: '접수번호',
    project_id:      'Project ID',
    status:          '상태',
    applied_date:    '신청일',
    expire_date:     '만료일',
    deadline_date:   '연장 마감',
    last_update:     '최근 갱신',
    closed_date:     '종료일',
    tier2:           '제안사 (Tier 2)',
    end_user:        '엔드유저',
    project_info:    '프로젝트 정보',
    pn:              'P/N',
    remark:          'Remark',
    application:     '애플리케이션',
    emp_id:          '담당자',
    owner_name:      '담당자 이름',
    note:            '비고',
    source_ref:      '엑셀 출처',
    expiry_notice_at: '만료 안내 발송일',
    // 연결 칸 (v2.143.0)
    sales_id:         '영업진행 건',
    tier2_company_id: '제안사 업체 (거래처 번호)',
    tier2_contact_id: '제안사 담당자 (주소록 번호)',
    created_by:      '등록자',
    updated_by:      '수정자',
    created_at:      '생성일시',
    updated_at:      '수정일시',
  },
  // 거래처 담당자 주소록 (v2.143.0)
  partner_contact: {
    partner_company_id: '거래처 (번호)',
    company_name:       '회사',
    company_name_en:    '회사 (영문)',
    name:               '이름',
    department:         '부서',
    title:              '직급',
    email:              '이메일',
    mobile:             '휴대전화',
    phone:              '전화',
    note:               '메모',
    is_active:          '사용',
    source:             '출처',
    source_ref:         '이관 출처',
    created_by:         '등록자',
    updated_by:         '수정자',
    created_at:         '생성일시',
    updated_at:         '수정일시',
  },
  // 딜등록 제안사 명단 · 정보 요청 (v2.141.0 · 명단은 v2.143.0 에 partner_contact 로 통합)
  deal_partner_contact: {
    company_kr:   '회사 (한글)',
    company_en:   '회사 (영문)',
    contact_name: '담당자',
    email:        '이메일',
    phone:        '전화',
    note:         '메모',
    is_active:    '사용',
    source_ref:   '엑셀 출처',
    created_by:   '등록자',
    updated_by:   '수정자',
    created_at:   '생성일시',
    updated_at:   '수정일시',
  },
  deal_info_request: {
    vendor:         '벤더',
    contact_id:     '명단 담당자 번호',
    company:        '회사',
    contact_name:   '담당자',
    to_emails:      '받는사람',
    end_user:       '엔드유저',
    project_name:   '프로젝트명',
    project_status: '진행 현황',
    deal_id:        '연결된 딜등록',
    sent_at:        '보낸 때',
    sent_by:        '보낸 사람',
    sent_by_name:   '보낸 사람 이름',
    note:           '메모',
    created_at:     '생성일시',
  },
  sales: {
    sales_id:           '매출일련번호',
    status:             '진행단계',
    emp_id:             '담당자',
    company_id:         '사업자',
    proposal_company:   '제안업체',
    end_user:           '엔드유저',
    invoice_company:    '계산서 발행업체',
    proposal_price:     '제안가',
    delivery_date:      '납품완료일',
    expected_delivery_date: '납품 예정일',
    next_step:          '다음 할 일',
    next_step_date:     '다음 할 일 예정일',
    next_step_at:       '다음 할 일 바꾼 시각',
    stage:              '진행단계',
    invoice_issued:     '계산서 발행 표시',
    report_done:        '판보 표시',
    invoice_date:       '계산서 발행일',
    project_name:       '프로젝트명',
    item_summary:       '제안품목',
    last_update:        '최종수정일',
    note:               '비고',
    created_at:         '생성일시',
    updated_at:         '수정일시',
    // v2.028 전 컬럼 기록으로 새로 이력에 나타나는 칸들
    contact_person:     '거래처 담당자',
    contract_date:      '계약일',
    delivery_place:     '납품장소',
    delivery_start_time: '납품 시작시간',
    delivery_end_time:  '납품 종료시간',
    report_status:      '비용참고',
    report_note:        '판매보고서 비고',
    cost_note:          '원가메모',
    is_track_record:    '실적증빙 매출',
    invoice_request_at: '계산서 발행요청 시각',
    invoice_request_by: '계산서 발행요청자',
    return_date:        '반품일',
    return_reason:      '반품사유',
    parent_sales_id:    '분할 원래 건',
  },
  purchase: {
    purchase_id:           '매입ID',
    sales_id:              '연결 영업번호',
    item_code:             '품목코드',
    description:           '품목 설명',
    supplier:              '매입처',
    order_qty:             '수량',
    unit_price:            '단가',
    amount:                '금액',
    order_date:            '발주일',
    actual_in_date:        '입고일',
    actual_out_date:       '출고일',
    purchase_invoice_date: '매입계산서 발행일',
    in_type:               '입고유형',
    out_type:              '출고유형',
    purchase_company_id:   '매입 사업자',
    invoice_company:       '계산서 발행업체',
    end_user:              '엔드유저',
    actual_sales_to:       '실제 사용처',
    project_name:          '건명',
    spec:                  '규격',
    category:              '유형',
    review_status:         '확인 상태',
    nts_approval_no:       '계산서번호',
    note:                  '메모',
    parent_purchase_id:    '분할 부모ID',
    created_at:            '생성일시',
    updated_at:            '수정일시',
    // v2.028 전 컬럼 기록으로 새로 이력에 나타나는 칸들
    item_category:         '품목분류',
    item_group:            '품목그룹',
    sales_amount:          '매출금액',
    sales_qty:             '출고수량',
    sales_company_id:      '매출 사업자',
    maintenance_sales_id:  '유지보수 매출 연결',
    maint_year:            '유지보수 연도',
    maint_month:           '유지보수 월',
    subcontract_id:        '하도급 연결',
    import_order_id:       '수입오더 연결',
    usd_list_price:        '정가(달러)',
    usd_unit_price:        '할인가(달러)',
    import_invoice_date:   '수입 Invoice 일자',
    import_remit_due_date: '송금기한',
    manufacture_id:        '제조 산출',
    consumed_by_manufacture_id: '제조 투입',
  },
  maintenance_sales: {
    maintenance_id:  '유지보수ID',
    contract_id:     '계약 번호',
    contract_no:     '계약번호',
    contract_name:   '계약명',
    end_user:        '엔드유저',
    contract_company: '청구업체',
    contract_start:  '계약 시작일',
    contract_end:    '계약 종료일',
    billing_cycle:   '청구주기',
    sales_amount:    '매출액',
    purchase_amount: '매입액',
    invoice_date:    '계산서 발행일',
    revenue_year:    '몇 년분',
    revenue_month:   '몇 월분',
    contract_qty:    '계약수량',
    emp_id:          '담당자',
    company_id:      '사업자',
    vendor_name:     '매입처',
    note:            '비고',
    created_at:      '생성일시',
    updated_at:      '수정일시',
  },
  // 유지보수 계약 (v2.279.0 전 칸 — 수정 창의 글자 그대로)
  maintenance_contract: {
    contract_id:        '계약 번호',
    contract_no:        '계약번호',
    contract_name:      '계약명(계산서 발행정보)',
    end_user:           '엔드유저(고객사)',
    contract_company:   '청구업체',
    award_company:      '확약/낙찰업체',
    company_id:         '사업자',
    emp_id:             '영업담당',
    status:             '상태',
    contract_start:     '계약 시작일',
    contract_end:       '계약 종료일',
    billing_cycle:      '청구주기',
    pm_name:            '유지보수 PM',
    engineer_name:      '담당 엔지니어',
    target_equipment:   '대상 장비',
    note:               '비고',
    prev_year_amount:   '전년 금액',
    year_amount:        '년 총액',
    invoiced_amount:    '발행금액',
    invoice_schedule:   '계산서 발행 약정',
    per_invoice_amount: '발행액(1회)',
    monthly_amount:     '월 금액',
    site_address:       '사이트 주소',
    maintenance_vendor: '유지보수 업체',
    site_contact:       '현장 담당자',
    site_contact_phone: '현장 연락처',
    invoice_plan:              '계산서 발행 날짜(종류)',
    invoice_plan_day:          '계산서 발행 날짜(매월 며칠)',
    invoice_plan_month_offset: '계산서 발행 날짜(몇 월분)',
    invoice_plan_dates:        '계산서 발행 날짜(날짜)',
    invoice_plan_dec_notice:   '12월분 별도 공지',
    invoice_email1:     '계산서 이메일 ①',
    invoice_email2:     '계산서 이메일 ②',
    bid_title:          '입찰공고 건명',
    agreement_amount:   '협약금액(부가세 포함)',
    award_rate:         '낙찰률(%)',
  },
  maintenance_year_amount: {
    contract_id:        '계약 번호',
    year:               '연도',
    amount:             '그해 금액',
    subcontract_amount: '하도급 금액',
  },
  maintenance_subcontract: {
    subcontract_id: '하도급 번호',
    contract_id:    '계약 번호',
    year:           '연도',
    supplier:       '매입처(하도급 업체)',
    item_name:      '품목',
    spec:           '규격',
    amount:         '금액(1회)',
    payment_cycle:  '지급주기',
    note:           '비고',
  },
  emergency_bell_sales: {
    bell_id:         '비상벨ID',
    site_name:       '현장명',
    end_user:        '엔드유저',
    sales_amount:    '매출금액',
    purchase_amount: '매입금액',
    invoice_date:    '발행일',
    device_count:    '장치수량',
    unit_price:      '단가',
    emp_id:          '담당자',
    company_id:      '사업자',
    note:            '비고',
    created_at:      '생성일시',
    updated_at:      '수정일시',
  },
  app_user: {
    user_id:        '사용자ID',
    emp_id:         '연결 직원',
    role:           '권한',
    security_level: '보안등급',
    is_active:      '활성여부',
    email:          '이메일',
    password_hash:  '비밀번호(해시)',
    totp_secret:    'TOTP 시크릿',
    created_at:     '생성일시',
    updated_at:     '수정일시',
  },
  employee: {
    emp_id:      '직원ID',
    name:        '이름',
    department:  '부서',
    position:    '직책',
    is_active:   '활성여부',
    company_id:  '사업자',
  },
  price_list: {
    price_id:         '가격ID',
    item_code:        '품목코드',
    supply_price_krw: '공급가',
    retail_price_krw: '소매가',
    status:           '상태',
    valid_from:       '적용시작일',
    valid_to:         '적용종료일',
    note:             '비고',
  },
  stock_release: {
    release_id:              '출고ID',
    release_type:            '출고유형',
    release_date:            '출고일',
    status:                  '상태',
    use_for_sales_id:        '사용처 영업번호',
    use_for_contract_id:     '사용처 계약',
    use_for_text:            '사용처(자유 입력)',
    use_for_end_user:        '엔드유저',
    use_for_proposal_company:'제안사',
    use_for_project_name:    '프로젝트명',
    requester_user_id:       '요청자',
    approved_by:             '승인자',
    approved_at:             '승인일시',
    note:                    '비고',
    created_at:              '생성일시',
    updated_at:              '수정일시',
  },
  stock_release_item: {
    release_item_id: '출고 항목ID',
    release_id:      '출고ID',
    item_code:       '품목코드',
    item_name:       '품목명',
    qty:             '수량',
    note:            '비고',
  },
  item_master: {
    item_code:     '품목코드',
    item_name:     '품목명',
    spec:          '사양',
    display_group: '분류 그룹',
    is_key_stock:  '주요재고 여부',
    note:          '비고',
  },
};

/** 공통 라벨 — 모든 테이블에서 자주 등장하는 메타 컬럼 fallback */
const COMMON_LABELS: Record<string, string> = {
  id:          'ID',
  created_at:  '생성일시',
  updated_at:  '수정일시',
  created_by:  '생성자',
  updated_by:  '수정자',
  note:        '비고',
  company_id:  '사업자',
  emp_id:      '담당자',
};

/** snake_case → 사람 친화 fallback (한글 매핑 없을 때만) */
function humanizeSnakeCase(s: string): string {
  return s.replace(/_/g, ' ');
}

/** 테이블 한글 메뉴명. 매핑 없으면 영문 그대로. */
export function getMenuLabel(tableName: string): string {
  return TABLE_META[tableName]?.menu ?? tableName;
}

/** 테이블+필드 → 한글 항목명. 매핑 없으면 fallback (공통 → snake_case humanize). */
export function getFieldLabel(tableName: string, fieldName: string | null): string {
  if (!fieldName) return '-';
  const map = FIELD_LABELS[tableName];
  if (map?.[fieldName]) return map[fieldName]!;
  if (COMMON_LABELS[fieldName]) return COMMON_LABELS[fieldName]!;
  return humanizeSnakeCase(fieldName);
}

/** 행 식별자 — row_data 에서 사람 친화적 식별 필드 추출 (PK 옆에 부가 표시용). */
export function getRowIdentifier(
  tableName: string,
  rowData: Record<string, unknown> | null | undefined,
): string | null {
  if (!rowData) return null;
  const fields = TABLE_META[tableName]?.identifierFields;
  if (!fields) return null;
  for (const f of fields) {
    const v = rowData[f];
    if (typeof v === 'string' && v.trim() !== '') return v;
  }
  return null;
}

/** 동작(INSERT/UPDATE/DELETE) → 한글 동사 */
export function getActionVerb(action: string): string {
  if (action === 'INSERT') return '입력';
  if (action === 'UPDATE') return '수정';
  if (action === 'DELETE') return '삭제';
  return action;
}

/** 값 사람 친화적 포맷 — 날짜 ISO, boolean, null 등 */
export function formatAuditValue(v: unknown): string {
  if (v === null || v === undefined) return '(없음)';
  if (typeof v === 'boolean') return v ? '예' : '아니오';
  // 트리거가 텍스트로 저장한 boolean (v2.028 전 컬럼 기록)
  if (v === 'true') return '예';
  if (v === 'false') return '아니오';
  if (typeof v === 'number') {
    // 큰 숫자는 천 단위 콤마 (단가/금액)
    if (Number.isInteger(v) && Math.abs(v) >= 1000) {
      return v.toLocaleString('ko-KR');
    }
    return String(v);
  }
  if (typeof v === 'string') {
    // 메모(sales.note) JSON 배열 → "메모 N건" 요약 (v2.028 전 컬럼 기록)
    if (v.startsWith('[')) {
      try {
        const arr = JSON.parse(v) as Array<{ content?: unknown }>;
        if (Array.isArray(arr) && arr.length > 0 && arr.every((m) => m && typeof m === 'object')) {
          return `메모 ${arr.length}건`;
        }
      } catch { /* JSON 아니면 원문 그대로 */ }
    }
    // ISO timestamp (YYYY-MM-DDTHH:MM:SS...) → 'YYYY-MM-DD HH:MM:SS' (시간 있을 때만)
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) {
        const yy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        return `${yy}-${mm}-${dd} ${hh}:${mi}`;
      }
    }
    // ISO date only → 그대로 (보기 쉬움)
    return v;
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
